import { DragDropProvider } from '@dnd-kit/react'
import { isSortable, useSortable } from '@dnd-kit/react/sortable'
import { GripVertical, Minus } from 'lucide-react'
import {
  moveWidget,
  setWidgetShown,
  setWidgetSize,
  setWidgetView,
  type WidgetPlacement,
} from '@/lib/dashboardLayouts'
import { WIDGET_SIZES, dashboardWidget, type WidgetSize, type WidgetView } from '@/lib/dashboardWidgets'
import { cn } from '@/lib/utils'
import { DashboardWidget } from './DashboardWidget'

const GRID = 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'

const SPAN: Record<WidgetSize, string> = {
  small: 'col-span-1',
  wide: 'col-span-1 md:col-span-2',
  full: 'col-span-full',
}

/**
 * A dashboard's widgets, in order. Callers pass only the widgets the user may
 * see; the grid does not check permissions itself.
 *
 * In edit mode the widgets can be dragged (the others move out of the way),
 * resized and removed. Keyboard users focus a widget's handle, press Space,
 * move it with the arrow keys and press Space again (dnd-kit's keyboard sensor).
 */
export function DashboardGrid({
  widgets,
  editing = false,
  onChange,
  onViewChange,
}: {
  widgets: WidgetPlacement[]
  editing?: boolean
  onChange?: (widgets: WidgetPlacement[]) => void
  /**
   * A widget's DEFAULT view, set by double-clicking it outside edit mode. The
   * page saves it at once: it is a preference, not a layout change worth a
   * Save step. A single click only changes the view for this visit.
   */
  onViewChange?: (id: string, view: WidgetView) => void
}) {
  if (!editing || !onChange) {
    return (
      <div className={GRID}>
        {widgets.map(w => (
          <div key={w.id} className={SPAN[w.size]}>
            <DashboardWidget placement={w} onMakeDefault={onViewChange && (view => onViewChange(w.id, view))} />
          </div>
        ))}
      </div>
    )
  }
  return (
    <DragDropProvider
      onDragEnd={event => {
        if (event.canceled) return
        const { source } = event.operation
        if (!isSortable(source)) return
        onChange(moveWidget(widgets, source.initialIndex, source.index))
      }}
    >
      <div className={GRID}>
        {widgets.map((w, index) => (
          <EditableWidget
            key={w.id}
            placement={w}
            index={index}
            onResize={size => onChange(setWidgetSize(widgets, w.id, size))}
            onView={view => onChange(setWidgetView(widgets, w.id, view))}
            onRemove={() => onChange(setWidgetShown(widgets, w.id, false))}
          />
        ))}
      </div>
    </DragDropProvider>
  )
}

function EditableWidget({
  placement,
  index,
  onResize,
  onView,
  onRemove,
}: {
  placement: WidgetPlacement
  index: number
  onResize: (size: WidgetSize) => void
  onView: (view: WidgetView) => void
  onRemove: () => void
}) {
  const { ref, handleRef, isDragging } = useSortable({ id: placement.id, index })
  const title = dashboardWidget(placement.id)?.title ?? placement.id
  return (
    <div
      ref={ref}
      className={cn(
        SPAN[placement.size],
        'relative rounded-xl outline-dashed outline-2 outline-offset-2 outline-blue-300',
        isDragging && 'z-10 opacity-80 shadow-lg',
      )}
    >
      <div className='absolute inset-x-2 top-2 z-10 flex items-center justify-between gap-2'>
        <div
          ref={handleRef}
          tabIndex={0}
          aria-label={`${title}. Press Space to move`}
          aria-roledescription='movable widget'
          title='Drag to move'
          className={cn(
            'flex h-7 w-7 cursor-grab touch-none select-none items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
            isDragging && 'cursor-grabbing',
          )}
        >
          <GripVertical className='h-4 w-4' />
        </div>
        <div className='flex items-center gap-1'>
          <div role='group' aria-label={`Size of ${title}`} className='flex rounded-md border border-gray-200 bg-white p-0.5 shadow-sm'>
            {WIDGET_SIZES.map(s => (
              <button
                key={s.id}
                type='button'
                aria-pressed={placement.size === s.id}
                title={s.label}
                onClick={() => onResize(s.id)}
                className={cn(
                  'rounded px-1.5 py-0.5 text-[11px] font-medium',
                  placement.size === s.id ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100',
                )}
              >
                {s.id === 'small' ? 'S' : s.id === 'wide' ? 'W' : 'F'}
              </button>
            ))}
          </div>
          <button
            type='button'
            onClick={onRemove}
            aria-label={`Remove ${title}`}
            title={`Remove ${title}`}
            className='flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-red-50 hover:text-red-600'
          >
            <Minus className='h-4 w-4' />
          </button>
        </div>
      </div>
      {/* The live widget sits under a veil, so a click inside it never follows a
          link mid-edit; only its view switcher stays usable. */}
      <div className='pt-8 [&_a]:pointer-events-none [&_section>div]:pointer-events-none [&_section>div]:opacity-70'>
        <DashboardWidget placement={placement} onViewChange={onView} />
      </div>
    </div>
  )
}
